import { useMutation } from "@apollo/client/react";
import { ArrowRight, Check, Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import NextImage from "next/image";
import NextLink from "next/link";
import type React from "react";
import { useState } from "react";
import { z } from "zod";
import { b } from "@/b";
import LOGO from "../../img/logo.webp";

const OptIntoEmail_Mutation = b.mutation("FooterOptIntoEmail", { email: "String!" }, (b, v) => [
  b.viewer((b) => [b.enrollInEmailMarketing({ email: v.email }, (b) => [b.id()])]),
]);

export const Footer: React.FC = () => {
  const [optIntoEmail] = useMutation(OptIntoEmail_Mutation.document());
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await optIntoEmail({ variables: { email } });
      if (result.data) {
        setSubscribed(true);
        setEmail("");
      }
    } catch {
      /* silent */
    }
    setLoading(false);
  };

  const isValidEmail = email && z.string().email().safeParse(email).success;

  return (
    <footer className="w-full border-t border-gray-100 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-14">
        {/* Top: Brand + socials + newsletter */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 pb-10 border-b border-gray-100">
          {/* Brand + socials inline */}
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-5">
            <NextLink href="/" className="flex items-center gap-2.5 no-underline shrink-0">
              <NextImage src={LOGO} alt="Visionary Meals" className="w-9 h-9" />
              <span className="text-base font-medium tracking-wider text-gray-700 uppercase">
                Visionary Meals
              </span>
            </NextLink>
            <span className="hidden sm:block w-px h-6 bg-gray-200" />
            <div className="flex items-center gap-2">
              <a
                href="https://www.instagram.com/visionary.meals"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center size-8 rounded-lg text-gray-400 hover:text-brand-black hover:bg-gray-100 transition-all"
                aria-label="Instagram"
              >
                <Instagram className="size-4" />
              </a>
              <a
                href="https://www.facebook.com/VisionaryMeals"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center size-8 rounded-lg text-gray-400 hover:text-brand-black hover:bg-gray-100 transition-all"
                aria-label="Facebook"
              >
                <Facebook className="size-4" />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="w-full sm:max-w-sm">
            {subscribed ? (
              <div className="flex items-center gap-3 rounded-xl bg-brand-mint/10 border border-brand-mint/20 px-4 py-3">
                <span className="flex items-center justify-center size-7 rounded-full bg-brand-mint shrink-0">
                  <Check className="size-3.5 text-brand-black" strokeWidth={3} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-brand-black">You're subscribed!</p>
                  <p className="text-[11px] text-gray-500">Watch your inbox for exclusive deals.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-brand-black placeholder:text-gray-400 outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint/30 transition-all"
                />
                <button
                  type="submit"
                  disabled={!isValidEmail || loading}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-all cursor-pointer active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                >
                  {loading ? (
                    "..."
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight className="size-3.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Links grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8 py-10">
          {/* Shop */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Shop
            </p>
            <ul className="space-y-2">
              {[
                { href: "/menu", label: "Menu" },
                { href: "/nutrition", label: "Nutrition" },
                { href: "/swag", label: "Swag" },
                { href: "/gift-cards", label: "Gift Cards" },
                { href: "/cart", label: "Cart" },
              ].map((link) => (
                <li key={link.href}>
                  <NextLink
                    href={link.href}
                    className="text-sm text-gray-600 hover:text-brand-black transition-colors no-underline"
                  >
                    {link.label}
                  </NextLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Company
            </p>
            <ul className="space-y-2">
              {[
                { href: "/our-story", label: "Our Story" },
                { href: "/meet-the-team", label: "Meet the Team" },
                { href: "/our-partners", label: "Our Partners" },
                { href: "/faq", label: "FAQ" },
              ].map((link) => (
                <li key={link.href}>
                  <NextLink
                    href={link.href}
                    className="text-sm text-gray-600 hover:text-brand-black transition-colors no-underline"
                  >
                    {link.label}
                  </NextLink>
                </li>
              ))}
            </ul>
          </div>

          {/* VIP */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
              VIP
            </p>
            <ul className="space-y-2">
              <li>
                <NextLink
                  href="/vip"
                  className="text-sm text-gray-600 hover:text-brand-black transition-colors no-underline"
                >
                  VIP Program
                </NextLink>
              </li>
              <li>
                <NextLink
                  href="/vip"
                  className="text-sm text-gray-600 hover:text-brand-black transition-colors no-underline"
                >
                  Save 25%
                </NextLink>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Contact
            </p>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="tel:7406395412"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand-black hover:underline transition-colors no-underline"
                >
                  <Phone className="size-3.5 text-gray-400 shrink-0" />
                  (740) 639-5412
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@visionarymeals.com"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand-black hover:underline transition-colors no-underline break-all"
                >
                  <Mail className="size-3.5 text-gray-400 shrink-0" />
                  info@visionarymeals.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin className="size-3.5 text-gray-400 shrink-0 mt-0.5" />
                Granville, OH 43023
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Visionary Meals · Gluten-Free · Farm-to-Table · Made in
            Ohio
          </p>
          <div className="flex items-center gap-4">
            <NextLink
              href="/privacy-policy"
              className="text-xs text-gray-400 hover:text-brand-black transition-colors no-underline"
            >
              Privacy Policy
            </NextLink>
            <NextLink
              href="/terms-of-service"
              className="text-xs text-gray-400 hover:text-brand-black transition-colors no-underline"
            >
              Terms of Service
            </NextLink>
          </div>
        </div>
      </div>
    </footer>
  );
};
